/* eslint-disable max-lines */
import {
  CategoryType,
  SearchAPIResponse,
} from '../../../../../../../search/search.types';
import {
  CASSFilter,
  CEFilter,
  parliamentaryQuestionsFilter,
} from '../../../utils/constants';
import { transformCount } from '../../../utils/utils';

/**
 * This is a temporary test in order to validate the user's need of that filter.
 * We currently display it on:
 * - Livre des procédures fiscales (LEGITEXT000006069583)
 * - Code général des impôts (LEGITEXT000006069577)
 */
const shouldDisplayBOFIPFilter = (id: string) =>
  id === 'LEGITEXT000006069583' || id === 'LEGITEXT000006069577';

export const shouldBeSortedByChrono = (
  value: string | { legislativeFolder: string },
) =>
  value === 'sort_chrono' ||
  value === 'parliamentary_questions' ||
  value === 'bofip';

export const buildArticleFilters = ({
  type,
  loggedIn,
  QPCDecisions,
  referenceDecisions,
  aggregations,
  intl,
  textId = '',
}: {
  type: string;
  loggedIn: boolean;
  QPCDecisions?: {}[];
  referenceDecisions?: {}[];
  aggregations: {
    pq?: { count: number };
    bofip?: { count: number };
    ce?: { count: number };
    cass?: { count: number };
  };
  intl: any;
  textId?: string;
}) => {
  return [
    {
      label: intl.formatMessage({
        id: 'page/components/SearchQuerySection.tsx/4',
      }),
      value: 'sort_relevance',
      selected: type === 'arret' || (type === 'commentary' && !loggedIn),
      combinedWithNext: true,
    },
    {
      label: intl.formatMessage({
        id: 'page/components/SearchQuerySection.tsx/5',
      }),
      value: 'sort_chrono',
      selected: type === 'commentary',
    },
    ...(QPCDecisions && QPCDecisions.length
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/2' },
              { number: transformCount(QPCDecisions.length) },
            ),
            value: 'qpc',
          },
        ]
      : []),
    ...(referenceDecisions && referenceDecisions.length
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/3' },
              {
                number: transformCount(referenceDecisions.length),
              },
            ),
            value: 'reference',
          },
        ]
      : []),
    ...(aggregations.pq && aggregations.pq.count > 0
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/7' },
              {
                number: transformCount(aggregations.pq.count),
              },
            ),
            value: 'parliamentary_questions',
          },
        ]
      : []),
    ...(shouldDisplayBOFIPFilter(textId) &&
    aggregations.bofip &&
    aggregations.bofip.count > 0
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/10' },
              {
                number: transformCount(aggregations.bofip.count),
              },
            ),
            value: 'bofip',
          },
        ]
      : []),
    ...(aggregations.cass && aggregations.cass.count > 0
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/8' },
              {
                number: transformCount(aggregations.cass.count),
              },
            ),
            value: 'cass',
          },
        ]
      : []),
    ...(aggregations.ce && aggregations.ce.count > 0
      ? [
          {
            label: intl.formatMessage(
              { id: 'page/components/SearchQuerySection.tsx/9' },
              {
                number: transformCount(aggregations.ce.count),
              },
            ),
            value: 'ce',
          },
        ]
      : []),
  ];
};

export const getParliamentaryQuestionsAggregation = (
  aggregations: SearchAPIResponse['aggregations'] | null,
): { count: number } => {
  if (!aggregations?.publisher) return { count: 0 };
  const findPQ =
    aggregations?.publisher
      .find(
        (item: CategoryType) =>
          item.key === 'Organismes administratifs et institutions',
      )
      ?.sub?.find(
        (item: CategoryType) => item.key === 'Questions parlementaires',
      )?.count || 0;
  return { count: findPQ };
};

export const getBOFIPAggregation = (
  aggregations: SearchAPIResponse['aggregations'] | null,
): { count: number } => {
  if (!aggregations?.publisher) return { count: 0 };
  const findBOFIP =
    aggregations?.publisher
      .find(
        (item: CategoryType) =>
          item.key === 'Organismes administratifs et institutions',
      )
      ?.sub?.find((item: CategoryType) => item.key === 'BOFIP')?.count || 0;
  return { count: findBOFIP };
};

export const getCASSAggregation = (
  aggregations: SearchAPIResponse['aggregations'] | null,
) => {
  if (!aggregations?.juridiction) return { count: 0 };
  const findCASS =
    aggregations?.juridiction.find((item) => item.key === 'CASS')?.count || 0;
  return { count: findCASS };
};

export const getCEAggregation = (
  aggregations: SearchAPIResponse['aggregations'] | null,
) => {
  if (!aggregations?.juridiction) return { count: 0 };
  const findCE =
    aggregations?.juridiction.find((item) => item.key === 'CE')?.count || 0;
  return { count: findCE };
};

export const getLegislativeFolderAggregation = (
  aggregations: SearchAPIResponse['aggregations'] | null,
): [] | { name: string; count: number }[] => {
  if (!aggregations?.pwLegislativeFolder) return [];
  return [
    ...aggregations?.pwLegislativeFolder.map((item) => ({
      name: item.categoryName,
      count: item.count,
    })),
  ];
};

export const getFilters = (value: string) =>
  (value === 'parliamentary_questions' && parliamentaryQuestionsFilter) ||
  (value === 'cass' && CASSFilter) ||
  (value === 'ce' && CEFilter) ||
  undefined;
